// src/components/DisciplineMemberActsPanel.js
//
// T5 / D-9 (2026-10-15) - the member's own voice in his discipline case (rulings 139, 176). The member the case names
// records a statement, an instruction to his representative, or a notice of different representation; everyone who
// reads the case reads them. Append-only and server-dated - the rules (disciplineCases/{id}/memberActs) enforce both.
// Who may record and the notices are src\data\disciplineMemberActs.js; this file decides nothing itself.
import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { addDoc, collection, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { MEMBER_ACT_KINDS, KIND_LABELS, TEXT_MAX, isNamedMember, validateMemberAct } from '../data/disciplineMemberActs';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };

export default function DisciplineMemberActsPanel({ disciplineCase, user }) {
  const k = disciplineCase || {};
  const caseId = k.id;
  const [acts, setActs] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [kind, setKind] = useState('statement');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!caseId || !k.status || k.status === 'draft') return undefined;
    const q = query(collection(db, 'disciplineCases', caseId, 'memberActs'), orderBy('at', 'desc'));
    return onSnapshot(q, snap => { setActs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadErr(''); },
      e => setLoadErr((e && (e.code || e.message)) || 'refused'));
  }, [caseId, k.status]);

  if (!caseId || !k.status || k.status === 'draft') return null;
  const me = auth.currentUser;
  const named = isNamedMember(k, { uid: user && user.uid, role: user && user.role, email: (user && user.email) || (me && me.email),
    emailVerified: !!(me && me.emailVerified) });

  const record = async () => {
    const v = validateMemberAct({ kind, text }, k);
    if (!v.ok) { setMsg({ ok: false, text: v.problems.join(' ') }); return; }
    setBusy(true); setMsg(null);
    try {
      await addDoc(collection(db, 'disciplineCases', caseId, 'memberActs'), {
        kind: v.value.kind, text: v.value.text, at: serverTimestamp(), byUid: user.uid, byName: String((user && user.name) || ''),
      });
      setText('');
      const gaps = [!k.scopeKey ? 'no Local Chairperson (the case carries no territory)' : '', !k.generalCommittee ? 'no General Chairperson (the case carries no committee)' : ''].filter(Boolean);
      setMsg({ ok: true, text: 'Recorded. The Local Chairperson and the General Chairperson\'s office are told.' + (gaps.length ? ' Not told: ' + gaps.join('; ') + '.' : '') });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>The member's own record</div>
      <div style={{ color: C.dim, fontSize: 11, margin: '2px 0 8px' }}>
        The member this case names records his statement, his instructions to his representative, or notice that he
        chooses different representation. Dated by the server; nothing here is edited or deleted.
      </div>
      {named ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
            {MEMBER_ACT_KINDS.map(t => (
              <label key={t} style={{ fontSize: 12, color: C.text }}>
                <input aria-label={KIND_LABELS[t]} type="radio" checked={kind === t} onChange={() => setKind(t)} /> {KIND_LABELS[t]}
              </label>
            ))}
          </div>
          <textarea aria-label="What the member said or wrote" className="form-input" rows={4} maxLength={TEXT_MAX} value={text} onChange={e => setText(e.target.value)} style={{ width: '100%' }} />
          <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={record} style={{ marginTop: 6 }}>
            {busy ? 'Recording...' : 'Record ' + KIND_LABELS[kind].toLowerCase()}
          </button>
          {msg ? <div style={{ fontSize: 12, marginTop: 4, color: msg.ok ? C.ok : C.bad }}>{msg.text}</div> : null}
        </div>
      ) : null}
      {loadErr ? <div style={{ color: C.bad, fontSize: 12 }}>The member's record could not be loaded ({loadErr}). This is a load failure, not an empty record.</div>
        : acts.length === 0 ? <div style={{ color: C.dim, fontSize: 12 }}>The member has recorded nothing on this case.</div>
        : acts.map(a => (
          <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line }}>
            <div style={{ fontSize: 11, color: a.kind === 'representation_notice' ? C.accent : C.dim }}>
              {KIND_LABELS[a.kind] || a.kind} - {a.byName || 'the member'} - {a.at && a.at.toDate ? a.at.toDate().toLocaleString() : 'date pending'}
            </div>
            <div style={{ fontSize: 12, color: C.text, whiteSpace: 'pre-wrap' }}>{a.text}</div>
          </div>
        ))}
    </div>
  );
}
